import { createSelector } from 'reselect'
import { getHeaderSelector, selectSearchConfig, getFooterSelector } from 'store/selectors/searchSelector'
import { LEADGEN_COPYRIGHT } from 'constants/copyRightConstant'
import { LEADGEN_DISCLAIMER } from 'constants/disclaimerConstant'
import { orderBy } from 'lodash'
import { replaceNbsps } from 'utils/functionUtils'

export const selectSearch = (state) => state.search
export const selectDetails = (state) => state.leadgen.details
export const selectLeadGen = (state) => state.leadgen
export const selectPartialReport = (state) => state.leadgen.partialReport
export const selectReportThreshold = (state) => state.leadgen.reportThreshold

export const getLeadGenHeaderSelector = createSelector(
  [getHeaderSelector, selectSearchConfig],
  (defaultHeaderProps, { preferences }) => ({
    ...defaultHeaderProps,
    menuStyles: {
      menuTextColor: preferences.menuTextColor,
      menuHoverColor: preferences.menuHoverColor,
      menuActiveColor: preferences.menuActiveColor
    }
  })
)

export const getBodySelector = createSelector(
  [selectSearchConfig, selectReportThreshold],
  (
    { preferences, ...rest },
    { data: { status, daysFromBillingDate }, status: { isLoading } }
  ) => ({
    additionalInfoText: preferences.additionalInfoText,
    buttonColor: preferences.buttonColor,
    buttonTextColor: preferences.buttonTextColor,
    contactPermissionText: rest.contactPermissionText,
    contactPermissionCheckboxText: rest.contactPermissionCheckboxText,
    daysFromBillingDate,
    enableContactPermission: rest.enableContactPermission,
    getReportButtonStyles: preferences.getReportButtonStyles,
    isLoading,
    isExceedReportThreshold: status !== undefined ? !status : !!status,
    leadGenTitle: preferences?.leadGenTitle,
    leadGenTitleFontColor: preferences.leadGenTitleFontColor,
    leadGenTextBelowTitle: preferences?.leadGenTextBelowTitle,
    leadGenTextBelowTitleFontColor: preferences.leadGenTextBelowTitleFontColor,
    questions: rest.questions,
    termsAndConditions: rest.termsAndConditions,
    termsAndConditionsCheckboxText: rest.termsAndConditionsCheckboxText,
    reportLimitText: rest.reportLimitText,
    sampleReportBannerBackgroundColor: preferences.sampleReportBannerBackgroundColor,
    sampleReportBannerButtonStyles: preferences.sampleReportBannerButtonStyles,
    sampleReportBannerStyles: preferences.sampleReportBannerStyles
  })
)

export const getConfigSelector = createSelector(
  [getLeadGenHeaderSelector, getBodySelector, getFooterSelector],
  (header, body, footer) => ({ header, body, footer })
)

export const getPropertySelector = createSelector(
  [selectSearch],
  ({ property }) => property
)

export const getLeadGenSelector = createSelector(
  [selectLeadGen],
  ({ details, otpStatus, reportCode, status: { isError, isLoading, msg, isLimitedError } }) => ({
    email: details.email || null,
    isError,
    isOtpSent: otpStatus === 'pending',
    isLoading,
    msg,
    reportCode,
    isLimitedError
  })
)

export const getPartialReportSelector = createSelector(
  [selectPartialReport],
  ({ data, status: { isError, isLoading } }) => ({
    photos: getPhotos(data.photos),
    coordinates: getCoordinates(data.map),
    disclaimer: getContent(LEADGEN_DISCLAIMER, data.disclaimer),
    copyright: getContent(LEADGEN_COPYRIGHT, data.copyright),
    blurredImageDesktop: data.blurredImageDesktop,
    blurredImageMobile: data.blurredImageMobile,
    isError,
    isLoading
  })
)

const getCoordinates = (map = {}) => {
  const { coordinates } = map
  return coordinates && (coordinates.latitude && coordinates.longitude)
    ? { lat: coordinates.latitude, lng: coordinates.longitude } : null
}

const getContent = (contents, data = []) => {
  const filteredContents = data.filter((item) => contents.includes(item.key))

  const contentString = [...orderBy(filteredContents, 'key', 'desc')]
    .reduce((acc, item) => {
      if (filteredContents.length < 2) {
        return acc + item.content
      }

      return acc + item.content + '<br/><br/>'
    }, '')

  return contentString
    ? replaceNbsps(contentString)
    : null
}

const getPhotos = (photos = []) => {
  const getDate = (date) => {
    return date ? new Date(date) : new Date()
  }
  return [...photos].sort((a, b) => getDate(b.scanDate) - getDate(a.scanDate))
    .reduce((acc, { digitalAssetType, isDefaultPhoto, largePhotoUrl, thumbnailPhotoUrl, scanDate }) => [...acc, {
      digitalAssetType,
      isDefaultPhoto,
      photoUrl: largePhotoUrl,
      thumbnailPhotoUrl,
      scanDate
    }], [])
}
